import React, { useEffect, useRef } from 'react';

import { Box, Typography, CircularProgress } from '@mui/material';

import { format, isSameDay } from 'date-fns';

import { useTalentSmsConversation } from '../hooks';
import { SmsConversationType } from '../types';
import SmsMessageBubble from './SmsMessageBubble';

interface SmsChatMessagesProps {
  conversation: SmsConversationType;
}

const SmsChatMessages: React.FC<SmsChatMessagesProps> = ({ conversation }) => {
  const { messages, loading } = useTalentSmsConversation(conversation.talent.id);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const renderDateDivider = (date: string) => {
    try {
      const messageDate = new Date(date);
      const today = new Date();
      const formattedDate = isSameDay(messageDate, today)
        ? 'Today'
        : format(messageDate, 'MM/dd/yyyy');

      return (
        <Box display="flex" justifyContent="center" alignItems="center" my={2}>
          <Box flex={1} height={0.1} bgcolor="#E0E0E0" mr={2} />
          <Typography fontSize={14} color="text.secondary" fontWeight={500}>
            {formattedDate}
          </Typography>
          <Box flex={1} height={0.1} bgcolor="#E0E0E0" ml={2} />
        </Box>
      );
    } catch {
      return null;
    }
  };

  const shouldShowDateDivider = (currentIndex: number) => {
    if (currentIndex === 0) return true;
    const currentDate = new Date(messages[currentIndex].created_at);
    const previousDate = new Date(messages[currentIndex - 1].created_at);
    return !isSameDay(currentDate, previousDate);
  };

  if (loading && messages.length === 0) {
    return (
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      flex={1}
      px={4}
      py={2}
      overflow="auto"
      display="flex"
      flexDirection="column"
    >
      {messages.length === 0 ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography color="text.secondary">
            No messages yet. Start the conversation!
          </Typography>
        </Box>
      ) : (
        <>
          {messages.map((message, index) => (
            <React.Fragment key={message.id}>
              {shouldShowDateDivider(index) &&
                renderDateDivider(message.created_at)}
              <SmsMessageBubble message={message} />
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </>
      )}
    </Box>
  );
};

export default SmsChatMessages;
